// Cart drawer (sections/cart-drawer.liquid): opens from the header cart icon, changes quantities and adds
// accessories through Shopify's AJAX cart API, then swaps in the drawer Shopify re-rendered in the same response
// (Section Rendering API). Without JS every control is a plain link or form to /cart.

const html = document.documentElement
const reduced = html.classList.contains('reduced')
const dlg = document.getElementById('CartDrawer')
const inner = dlg.querySelector('[data-cart-inner]')
const status = dlg.querySelector('[data-cart-status]')
const error = dlg.querySelector('[data-cart-error]')
const opener = document.querySelector('[data-cart-open]')
const root = window.Shopify?.routes?.root || '/'
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
let busy = false

function open() {
  if (dlg.open) return
  dlg.showModal()
  window.janado?.lenis?.stop()
  if (!reduced) dlg.animate([{ transform: 'translateX(100%)' }, { transform: 'none' }], { duration: 800, easing: EASE })
}

// Leave offer (snippets/cart-contents.liquid): the first attempt to close the drawer shows the offer instead.
// It counts down from 5 minutes and then disappears for real: the start time is kept in localStorage, so a reload,
// a new tab or the next visit neither restarts nor brings it back (the countdown must be true, UWG Annex No. 7).
// The same browser gets a new chance after 30 days.
const OFFER = 'janado:cart-offer'
const OFFER_MS = 5 * 60 * 1000
const OFFER_AGAIN_MS = 30 * 24 * 60 * 60 * 1000
const offerAt = () => {
  try {
    return Number(localStorage.getItem(OFFER)) || 0
  } catch (e) {
    return -1 // no storage: no countdown we could keep, so no offer
  }
}
const offerSeen = () => offerAt() < 0 || (offerAt() > 0 && Date.now() - offerAt() < OFFER_AGAIN_MS)
const offerLeft = () => (offerAt() > 0 ? offerAt() + OFFER_MS - Date.now() : 0)

/** Update the countdown; hides the offer for good once the time is up. */
function tick() {
  const o = inner.querySelector('[data-offer]:not([hidden])')
  if (!o) return
  const left = offerLeft()
  if (left <= 0) return (o.hidden = true)
  o.style.setProperty('--left', left / OFFER_MS)
  const s = Math.ceil(left / 1000)
  o.querySelector('[data-offer-time]').textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
setInterval(tick, 1000)

/** Reveal the offer if the cart qualifies and its time isn't up. Returns true if it was newly shown. */
function showOffer(animate = true) {
  const o = inner.querySelector('[data-offer]')
  if (!o) return false
  if (!offerSeen()) {
    try {
      localStorage.setItem(OFFER, String(Date.now()))
    } catch (e) {}
  }
  if (offerLeft() <= 0 || !o.hidden) return false
  o.hidden = false
  tick()
  if (animate && !reduced) o.animate([{ opacity: 0, transform: 'translateY(16px) scale(0.96)' }, { opacity: 1, transform: 'none' }], { duration: 500, easing: EASE })
  return true
}

function close(instant = false) {
  if (!dlg.open) return
  if (!instant && !offerSeen() && showOffer()) return
  if (instant || reduced) return dlg.close()
  dlg.animate([{ transform: 'none' }, { transform: 'translateX(100%)' }], { duration: 450, easing: 'cubic-bezier(0.5, 0, 0.75, 0)' }).onfinish = () => dlg.close()
}

/** Swap in the re-rendered drawer and sync the header count. */
function render(sectionHtml) {
  const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[data-cart-inner]')
  if (!fresh) return
  inner.innerHTML = fresh.innerHTML
  if (offerSeen()) showOffer(false)
  const cart = inner.querySelector('.cart')
  const n = Number(cart.dataset.count)
  const count = opener?.querySelector('[data-cart-count]')
  if (count) {
    count.textContent = n
    count.hidden = !n
  }
  opener?.setAttribute('aria-label', cart.dataset.label)
}

/** POST to an AJAX cart endpoint and re-render. Resolves to the response JSON, or null on error. */
async function send(path, body, message) {
  if (busy) return null
  busy = true
  inner.setAttribute('aria-busy', 'true')
  error.hidden = true
  try {
    const r = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }),
    })
    const json = await r.json()
    if (!r.ok) throw new Error(json.description || json.message)
    render(json.sections['cart-drawer'])
    status.textContent = message
    return json
  } catch (e) {
    error.textContent = e.message || dlg.dataset.msgError
    error.hidden = false
    return null
  } finally {
    busy = false
    inner.removeAttribute('aria-busy')
  }
}

/** Add a variant; the cart blob in the header pops. Product pages call this, then open(). */
async function add(id, quantity = 1, properties) {
  const json = await send('cart/add.js', { items: [{ id: Number(id), quantity, ...(properties && { properties }) }] }, dlg.dataset.msgAdded)
  const blob = opener?.querySelector('[data-blob]')
  if (json && blob && !reduced) {
    blob.dataset.blobScale = '1.4'
    setTimeout(() => (blob.dataset.blobScale = '0'), 450)
  }
  return json
}

opener?.addEventListener('click', (e) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
  e.preventDefault() // stops the page transition in motion.js too
  open()
})
dlg.addEventListener('cancel', (e) => {
  e.preventDefault() // Esc slides out like the close button
  close()
})
dlg.addEventListener('close', () => {
  error.hidden = true
  window.janado?.lenis?.start()
})
dlg.addEventListener('click', (e) => {
  if (e.target === dlg || e.target.closest('[data-cart-close]')) return close()
  if (e.target.closest('[data-offer-apply]')) {
    // the cart takes the code itself (AJAX cart API), then straight on to the checkout like the old pop-up did:
    // the decision is made in this moment, every step in between is a chance to drop off
    return send('cart/update.js', { discount: inner.querySelector('[data-offer]').dataset.offer }, dlg.dataset.msgUpdated).then(
      (json) => json && location.assign(`${root}checkout`)
    )
  }
  const q = e.target.closest('a[data-line]')
  if (q) {
    e.preventDefault()
    return send('cart/change.js', { line: Number(q.dataset.line), quantity: Number(q.dataset.qty) }, dlg.dataset.msgUpdated)
  }
  // Any other link leaves the page: drop the drawer at once so the page transition is visible
  if (e.target.closest('a[href]')) close(true)
})
dlg.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-cart-add]')
  if (!form) return // checkout submits normally
  e.preventDefault()
  add(form.elements.id.value)
})

// With a mouse, heading for the top edge (tabs, back button) counts as leaving too.
document.addEventListener('mouseout', (e) => {
  if (dlg.open && !e.relatedTarget && e.clientY <= 0 && !offerSeen()) showOffer()
})
// Touch: leaving for another app or tab with a qualifying cart counts as leaving; on return the drawer opens
// with the offer. The drawer markup is rendered with every page, so the hidden offer says whether the cart qualifies.
let away = false
document.addEventListener('visibilitychange', () => {
  if (html.classList.contains('fine') || offerSeen() || !inner.querySelector('[data-offer]')) return
  if (document.hidden) away = true
  else if (away && !html.classList.contains('leaving')) {
    away = false
    open()
    showOffer()
  }
})
// Rendered with the page: a drawer opened later in the session shows the offer it already got.
if (offerSeen()) showOffer(false)

// Line images are lazy and the closed drawer isn't rendered, so warm them once the page is idle.
const warm = () => inner.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'))
addEventListener('load', () => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 3000 }) : setTimeout(warm, 1500)), { once: true })

const j = (window.janado = window.janado || {})
/** Set the quantity of one cart line by its key (0 removes it), e.g. the product page's charger switch. */
const change = (key, quantity) => send('cart/change.js', { id: key, quantity }, dlg.dataset.msgUpdated)

j.cart = { open, close, add, change }
